import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { SERVER_URL } from '../../../DB_Url';
import vcontrolAPI from '../../../services/documentApi';
const applyHardwareTint = (canvas, context, tintColor) => {
  if (!tintColor) return;

  // 1. Create a copy of the original PDF canvas to use as an alpha mask
  // This preserves both transparent background pixels and white background pixels.
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = canvas.width;
  maskCanvas.height = canvas.height;
  const maskCtx = maskCanvas.getContext('2d');
  maskCtx.drawImage(canvas, 0, 0);

  // 2. Tint the dark pixels to the target color (and leave white as white)
  // 'screen' mode: Black + Color = Color, White + Color = White
  context.globalCompositeOperation = 'screen';
  context.fillStyle = tintColor;
  context.fillRect(0, 0, canvas.width, canvas.height);

  // 3. Restore the original alpha channel so transparent areas don't become solid color
  context.globalCompositeOperation = 'destination-in';
  context.drawImage(maskCanvas, 0, 0);

  // 4. Reset composite operation
  context.globalCompositeOperation = 'source-over';
};

// Detect low-end devices once at module load (hardware doesn't change mid-session).
// Uses CPU cores and RAM — catches low-end laptops, Chromebooks, tablets, and phones.
const isLowEndDevice = (() => {
  const cores = navigator.hardwareConcurrency || 4;
  // navigator.deviceMemory is Chrome-only; defaults to 8 when unavailable
  const memoryGB = navigator.deviceMemory || 8;
  return cores <= 4 || memoryGB <= 4;
})();

// PDF stream failure retry policy. Failures here are usually transient
// (network blips, a signed URL that expired, or a B2/API hiccup), so each
// retry re-fetches a FRESH signed URL instead of replaying the dead one.
// Every failure and recovery is reported to Firebase Analytics so recurring
// causes (e.g. documents whose files were deleted in B2) can be studied.
const MAX_PDF_RETRIES = 3;
const PDF_RETRY_DELAYS_MS = [1000, 3000, 8000];
// Firebase Analytics caps string params at 100 chars.
const MAX_ANALYTICS_PARAM_LENGTH = 100;

// Helper to safely close ImageBitmap after React and Konva have unmounted it
const safeCloseBitmap = (bitmap) => {
  if (bitmap && typeof bitmap.close === 'function') {
    // 500ms allows React to unmount and Konva to flush pending renders
    setTimeout(() => {
      try {
        bitmap.close();
      } catch (e) {
        // ignore
      }
    }, 500);
  }
};

export const usePdfStreaming = (currentPageData, containerDimensions, options = {}) => {
  const debounceMs = options.debounceMs || 120;
  const isInteracting = options.isInteracting || false;
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(''); // New status message state
  const [pdfError, setPdfError] = useState(null);

  // --- PDF stream retry + analytics state ---
  const [pdfRetryToken, setPdfRetryToken] = useState(0);
  const pdfRetryCountRef = useRef(0);
  const pdfRetryTimeoutRef = useRef(null);
  // Bumped whenever the target document changes (or the hook unmounts) so a
  // queued retry can never fire for a stale document.
  const pdfAttemptIdRef = useRef(0);
  const prevPdfDocIdRef = useRef(null);

  // High-res rendering status (for UI indicator on capable devices)
  const [isRenderingHighRes, setIsRenderingHighRes] = useState(false);

  // Base Low-Res Image (stored as ImageBitmap for GPU-accelerated compositing)
  const [basePdfImage, setBasePdfImage] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });

  // High-Res Overlay State (stored as ImageBitmap for GPU-accelerated compositing)
  const [highResImage, setHighResImage] = useState(null);
  const [highResBounds, setHighResBounds] = useState(null);
  const highResRenderTask = useRef(null);
  const renderTimeout = useRef(null);
  const patchCache = useRef(new Map()); // RAM Cache for high-res patches (ImageBitmaps)
  // Monotonic id for each pdf.js load run. Lets a superseded load's finally
  // skip clearing isPdfLoading (so it can't steal the flag from a newer run),
  // while the most recent run always clears it — even after cleanup flipped
  // `isSubscribed`, so a navigation away from a stalled load can't leave the
  // spinner stuck forever.
  const pdfLoadRunRef = useRef(0);

  // GPU Memory Cleanup: close previous ImageBitmaps when they are replaced
  const prevBaseBitmapRef = useRef(null);
  const prevHighResBitmapRef = useRef(null);

  const setBasePdfImageWithCleanup = useCallback((newImage) => {
    setBasePdfImage((prev) => {
      // Close old ImageBitmap to release GPU memory
      if (prev && prev !== newImage) {
        safeCloseBitmap(prev);
      }
      return newImage;
    });
  }, []);

  const setHighResImageWithCleanup = useCallback((newImage) => {
    setHighResImage((prev) => {
      if (prev && prev !== newImage) {
        safeCloseBitmap(prev);
      }
      return newImage;
    });
  }, []);

  const clearHighResImage = () => {
    setHighResImageWithCleanup(null);
  };

  // Report a PDF stream event to Firebase Analytics (best-effort; analytics
  // failures must never break the PDF pipeline).
  const logPdfStreamEvent = useCallback((eventName, extra = {}) => {
    try {
      const analytics = options.firebase?.analytics;
      if (!analytics || typeof analytics.logEvent !== 'function') return;
      const truncate = (value) => {
        const s = typeof value === 'string' ? value : String(value ?? '');
        return s.length > MAX_ANALYTICS_PARAM_LENGTH
          ? s.slice(0, MAX_ANALYTICS_PARAM_LENGTH)
          : s;
      };
      analytics.logEvent(eventName, {
        document_id: truncate(currentPageData?.documentId || ''),
        organization: truncate(currentPageData?.organization || ''),
        version: truncate(currentPageData?.version || ''),
        project: truncate(options.projectName || ''),
        ...Object.fromEntries(
          Object.entries(extra).map(([key, value]) => [
            key,
            truncate(value),
          ]),
        ),
      });
    } catch (e) {
      // Analytics must never break the PDF pipeline.
    }
  }, [
    options.firebase,
    options.projectName,
    currentPageData?.documentId,
    currentPageData?.organization,
    currentPageData?.version,
  ]);

  // Reset the retry budget whenever the target document changes, and cancel
  // any pending retry so it can't fire for a stale document.
  useEffect(() => {
    const docId = currentPageData?.documentId || null;
    if (docId !== prevPdfDocIdRef.current) {
      prevPdfDocIdRef.current = docId;
      pdfRetryCountRef.current = 0;
      pdfAttemptIdRef.current += 1;
      if (pdfRetryTimeoutRef.current) {
        clearTimeout(pdfRetryTimeoutRef.current);
        pdfRetryTimeoutRef.current = null;
      }
    }
  }, [currentPageData?.documentId]);

  // Queue a bounded retry of the PDF stream after a failure. Each retry bumps
  // pdfRetryToken, which makes the URL-fetch effect re-run and mint a fresh
  // signed URL (so an expired signature heals itself).
  const schedulePdfRetry = useCallback((reason) => {
    const attemptId = pdfAttemptIdRef.current;
    const attemptNumber = pdfRetryCountRef.current + 1;

    logPdfStreamEvent('replan_pdf_stream_error', {
      error_message: reason,
      attempt: attemptNumber,
      max_attempts: MAX_PDF_RETRIES,
    });

    if (attemptNumber > MAX_PDF_RETRIES) {
      // Budget exhausted — leave pdfError visible so the WebP fallback takes over.
      return;
    }

    const delay =
      PDF_RETRY_DELAYS_MS[attemptNumber - 1] ??
      PDF_RETRY_DELAYS_MS[PDF_RETRY_DELAYS_MS.length - 1];
    pdfRetryTimeoutRef.current = setTimeout(() => {
      pdfRetryTimeoutRef.current = null;
      if (pdfAttemptIdRef.current !== attemptId) return; // doc changed / unmounted
      pdfRetryCountRef.current = attemptNumber;
      setPdfRetryToken((token) => token + 1);
    }, delay);
  }, [logPdfStreamEvent]);

  // Clear any pending retry timer on unmount.
  useEffect(() => {
    return () => {
      if (pdfRetryTimeoutRef.current) {
        clearTimeout(pdfRetryTimeoutRef.current);
        pdfRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // Fetch the PDF URL when the document changes
  useEffect(() => {
    // HARD RESET: Clear previous PDF data IMMEDIATELY when documentId changes
    // This prevents "ghosting" from previous projects while fetching new URL
    setPdfUrl(null);
    setBasePdfImageWithCleanup(null);
    setHighResImageWithCleanup(null);
    setPdfError(null);
    // Close all cached ImageBitmaps before clearing the cache
    for (const entry of patchCache.current.values()) {
      safeCloseBitmap(entry.image);
    }
    patchCache.current.clear();

    if (!currentPageData || !currentPageData.documentId) {
      return;
    }

    // Don't fetch PDF URL while stream is paused (WebP hasn't loaded yet,
    // or user hasn't zoomed past threshold). This prevents the PDF download
    // from competing with the WebP image for bandwidth.
    if (options.pauseStream) {
      return;
    }
    const controller = new AbortController();
    const fetchPdfUrl = async () => {
      setIsPdfLoading(true);
      setLoadingStatus('Fetching PDF access...');
      setPdfError(null);
      try {
        const response = await vcontrolAPI.fetchWithRetry(
          `${SERVER_URL}/api/document/${currentPageData.documentId}/pdf-url`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error(`Failed to fetch PDF URL endpoint: ${response.status}`);
        const data = await response.json();

        if (data.url) {
          setPdfUrl(data.url);
        } else if (data.error) {
          setPdfError(data.error);
          schedulePdfRetry(data.error);
        } else {
          setPdfError('Unexpected response format');
          schedulePdfRetry('Unexpected response format');
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        const message = `Failed to retrieve stream URL: ${err.message}`;
        setPdfError(message);
        // A retry re-fetches a FRESH signed URL, which also heals expired
        // signatures instead of replaying the dead URL.
        schedulePdfRetry(message);
      } finally {
        setIsPdfLoading(false);
      }
    };
    fetchPdfUrl();
    return () => controller.abort();
  }, [currentPageData?.documentId, options.pauseStream, pdfRetryToken]);

  // Load the PDF via pdfjs
  useEffect(() => {
    if (!pdfUrl) {
      setPdfDoc(null);
      // Clear the loading flag when the document goes away (e.g. switching to
      // a project with no plans). Without this, a PDF load left in flight by a
      // navigation keeps isPdfLoading true forever and the app shows an
      // infinite spinner instead of the empty ("add plan") state.
      setIsPdfLoading(false);
      return;
    }
    let isSubscribed = true;
    let loadingTask = null;
    const loadRunId = ++pdfLoadRunRef.current;
    const loadPdfDoc = async () => {
      setIsPdfLoading(true);
      setLoadingStatus('Initializing PDF stream...');
      try {
        loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          // RANGE REQUEST OPTIMIZATION:
          // Forcing disableStream: true ensures we use XHR instead of the browser Fetch Stream API.
          // XHR is significantly more reliable for range-requests on certain S3-compatible providers (like B2).
          disableAutoFetch: true,
          disableStream: true,
          disableRange: false,
          verbosity: 0
        });
        const pdf = await loadingTask.promise;
        if (isSubscribed) {
          // The stream recovered after retries — log it so we can measure how
          // often retries actually save a session, then reset the budget.
          if (pdfRetryCountRef.current > 0) {
            logPdfStreamEvent('replan_pdf_stream_recovered', {
              error_message: 'pdf_load_succeeded_after_retry',
              retries: pdfRetryCountRef.current,
            });
            pdfRetryCountRef.current = 0;
          }
          setPdfDoc(pdf);
        }
      } catch (err) {
        if (isSubscribed) {
          const message = `Failed to load PDF document: ${err.message}`;
          setPdfError(message);
          // Aborts (navigation / cleanup destroy) are not failures worth
          // retrying — the new document's own load will run on its own.
          const isAbort =
            err?.name === 'AbortError' ||
            String(err?.message || '').includes('Loading cancelled');
          if (!isAbort) {
            schedulePdfRetry(message);
          }
        }
      } finally {
        // Always clear the flag when THIS run is still the latest one — even
        // if cleanup already flipped isSubscribed — so a load superseded by
        // navigation cannot leave isPdfLoading stuck at true. Superseded runs
        // skip the clear so they never steal the flag from a newer load.
        if (loadRunId === pdfLoadRunRef.current) {
          setIsPdfLoading(false);
        }
      }
    };
    loadPdfDoc();
    return () => {
      isSubscribed = false;
      // Actually cancel a still-pending pdf.js load instead of letting it keep
      // churning (and possibly stalling forever) in the background after the
      // document changes or the component navigates away.
      try {
        loadingTask?.destroy();
      } catch (e) {
        // Already completed/destroyed — nothing left to cancel.
      }
    };
  }, [pdfUrl]);

  // 1. Initial BASE rendering (Lightning Fast 1.0x Scale)
  useEffect(() => {
    if (!pdfDoc || !currentPageData) return;

    // CLEAR the base image immediately on page change to prevent "ghosting"
    setBasePdfImageWithCleanup(null);
    setHighResImageWithCleanup(null);

    if (options.pauseStream) {
      return; // Wait for WebP to load before starting heavy PDF rendering
    }

    let renderTask = null;
    let isSubscribed = true;

    const renderBasePage = async () => {
      setIsPdfLoading(true);
      setLoadingStatus(`Rendering page ${currentPageData.actualPageNumber}...`);
      setPdfError(null);

      try {
        const pageNum = parseInt(currentPageData.actualPageNumber, 10);
        if (isNaN(pageNum) || pageNum < 1 || pageNum > pdfDoc.numPages) {
          throw new Error(`Requested page ${pageNum} is out of bounds`);
        }

        const page = await pdfDoc.getPage(pageNum);
        if (!isSubscribed) return;

        const unscaledViewport = page.getViewport({ scale: 1.0 });

        // ADAPTIVE PIXEL BUDGET: Instead of a fixed scale multiplier, cap the base
        // canvas to a maximum number of pixels. Mobile GPUs have far less VRAM and
        // slower CPUs — a 40M pixel canvas that's fine on a laptop will freeze a phone.
        const dpr = window.devicePixelRatio || 1;
        const MAX_PIXELS = isLowEndDevice ? 6_000_000 : 40_000_000; // 6M low-end, 25M high-end

        const pdfPixels = unscaledViewport.width * unscaledViewport.height;
        // Scale that fits within the budget, but at least 1.0x
        const budgetScale = Math.max(1.0, Math.sqrt(MAX_PIXELS / pdfPixels));
        // On desktop, also ensure we're at least at device pixel ratio for crispness
        const baseScale = isLowEndDevice ? Math.min(budgetScale, dpr) : Math.min(Math.max(budgetScale, dpr), 4.0);

        const hdViewport = page.getViewport({ scale: baseScale });
        const hdCanvas = document.createElement('canvas');
        hdCanvas.height = hdViewport.height;
        hdCanvas.width = hdViewport.width;
        const hdContext = hdCanvas.getContext('2d', { desynchronized: true });
        hdContext.imageSmoothingEnabled = false;
        hdContext.mozImageSmoothingEnabled = false;
        hdContext.webkitImageSmoothingEnabled = false;
        hdContext.msImageSmoothingEnabled = false;

        const hdRenderContext = {
          canvasContext: hdContext,
          viewport: hdViewport,
          background: 'rgba(255,255,255,0)',
          disableFontFace: false,
          fontExtraProperties: true,
          overprint: true
        };

        renderTask = page.render(hdRenderContext);
        await renderTask.promise;

        applyHardwareTint(hdCanvas, hdRenderContext.canvasContext, options.tintColor);

        if (isSubscribed) {
          setPdfDimensions({ width: unscaledViewport.width, height: unscaledViewport.height });

          // Convert to ImageBitmap for GPU-accelerated compositing.
          // ImageBitmaps are composited by the browser's compositor thread,
          // bypassing the main-thread canvas-to-canvas copy that causes pan lag.
          const bitmap = await createImageBitmap(hdCanvas);
          if (!isSubscribed) { bitmap.close(); return; }
          setBasePdfImageWithCleanup(bitmap);
        }
      } catch (err) {
        if (isSubscribed) setPdfError(`Failed to render base page content: ${err.message}`);
      } finally {
        if (isSubscribed) setIsPdfLoading(false);
      }
    };

    renderBasePage();
    return () => {
      isSubscribed = false;
      if (renderTask) renderTask.cancel();
    };
  }, [pdfDoc, currentPageData?.actualPageNumber, currentPageData?.documentId, options.pauseStream]);

  // INTERACTION GATE: Immediately kill all pending PDF work when user starts interacting.
  // This is the critical path — pdf.js page.render() blocks the main thread, so we must
  // cancel it the instant a zoom/pan gesture begins to keep 60fps.
  useEffect(() => {
    if (isInteracting) {
      // 1. Cancel any debounced render that hasn't fired yet
      if (renderTimeout.current) {
        clearTimeout(renderTimeout.current);
        renderTimeout.current = null;
      }
      // 2. Cancel any in-flight pdf.js render task that IS running on the main thread
      if (highResRenderTask.current) {
        try {
          highResRenderTask.current.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
        highResRenderTask.current = null;
      }
    }
  }, [isInteracting]);

  // 2. High-Res Sector Rendering Callback (Triggered dynamically from Replan.js)
  const updateHighResViewport = useCallback((stageScale, stagePos, isComparisonMode = false, coordinateScale = 1.0) => {
    // Hard gate: never schedule work while user is actively interacting
    if (isInteracting) return;
    if (!pdfDoc || !currentPageData || !basePdfImage || !stagePos) return;

    // NO LONGER CLEARING: Keep the previous high-res patch visible during motion
    // It will be replaced only when the new one is ready.

    // LOW-END DEVICES: Skip high-res sectors entirely. Phone screens have
    // enough physical pixel density (~400+ PPI) that the base layer looks
    // sharp at any zoom. High-res sectors only benefit large desktop monitors.
    if (isLowEndDevice) return;

    // Adaptive threshold based on device capability
    const hiResThreshold = 3.0;
    if (stageScale <= hiResThreshold) {
      setHighResImageWithCleanup(null);
      return;
    }

    // Immediately cancel any currently running render task before we even start the debounce timer
    // This is the critical fix for freeing the CPU instantly when the user starts panning/zooming.
    if (highResRenderTask.current) {
      try {
        highResRenderTask.current.cancel();
      } catch (e) {
        // Ignore cancellation errors
      }
      highResRenderTask.current = null;
    }

    if (renderTimeout.current) clearTimeout(renderTimeout.current);

    // BALANCED TIMEOUT: Uses debounceMs (default 120ms) to lower CPU consumption.
    // The overlay can use a higher debounce to prevent UI freezing during dual-renders.
    renderTimeout.current = setTimeout(async () => {
      setIsRenderingHighRes(true);
      try {
        const pageNum = parseInt(currentPageData.actualPageNumber, 10);

        // 1. Calculate the exact visible viewport in PDF points
        const visibleX = Math.floor(Math.max(0, (-stagePos.x / stageScale) * coordinateScale));
        const visibleY = Math.floor(Math.max(0, (-stagePos.y / stageScale) * coordinateScale));
        const visibleWidth = Math.ceil(Math.min(
          pdfDimensions.width - visibleX,
          ((containerDimensions?.width || window.innerWidth) / stageScale) * coordinateScale
        ));
        const visibleHeight = Math.ceil(Math.min(
          pdfDimensions.height - visibleY,
          ((containerDimensions?.height || window.innerHeight) / stageScale) * coordinateScale
        ));

        if (visibleWidth <= 0 || visibleHeight <= 0) return;

        // 2. PREDICTIVE TILING: Render a 10% margin around the viewport to handle small pans instantly (reduced from 25% to speed up rendering)
        const marginX = visibleWidth * 0.10;
        const marginY = visibleHeight * 0.10;
        // Quantize to 16px grid to dramatically increase cache hit rate
        const renderX = Math.floor(Math.max(0, visibleX - marginX) / 16) * 16;
        const renderY = Math.floor(Math.max(0, visibleY - marginY) / 16) * 16;
        const renderWidth = Math.ceil(Math.min(pdfDimensions.width - renderX, visibleWidth + (marginX * 2)) / 16) * 16;
        const renderHeight = Math.ceil(Math.min(pdfDimensions.height - renderY, visibleHeight + (marginY * 2)) / 16) * 16;

        const dpr = window.devicePixelRatio || 1;
        // Snap scale to 0.5 increments to increase cache hits
        const rawScale = (stageScale / coordinateScale) * dpr;
        const maxSectorScale = isLowEndDevice ? 3.0 : 5.0;
        // Snap to 0.5 increments for better cache reuse (Math.round only takes 1 arg)
        const targetOutputScale = Math.min(Math.max(Math.round(rawScale * 2) / 2, 2.0), maxSectorScale);


        // 3. CACHE CHECK: If we already rendered this exact tile, show it instantly
        const cacheKey = `${pageNum}-${renderX}-${renderY}-${renderWidth}-${renderHeight}-${targetOutputScale}`;
        if (patchCache.current.has(cacheKey)) {
          const cached = patchCache.current.get(cacheKey);
          setHighResBounds(cached.bounds);
          setHighResImage(cached.image);
          return;
        }

        const page = await pdfDoc.getPage(pageNum);
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(renderWidth * targetOutputScale);
        canvas.height = Math.ceil(renderHeight * targetOutputScale);
        const context = canvas.getContext('2d', { desynchronized: true });
        context.imageSmoothingEnabled = false; // Force sharp pixels
        context.mozImageSmoothingEnabled = false;
        context.webkitImageSmoothingEnabled = false;
        context.msImageSmoothingEnabled = false;

        const viewport = page.getViewport({
          scale: targetOutputScale,
          offsetX: -renderX * targetOutputScale,
          offsetY: -renderY * targetOutputScale
        });

        const renderContext = {
          canvasContext: context,
          viewport,
          background: 'rgba(255,255,255,0)',
          intent: 'print', // Use high-precision geometry
          fontExtraProperties: true,
          overprint: true
        };

        highResRenderTask.current = page.render(renderContext);
        await highResRenderTask.current.promise;

        applyHardwareTint(canvas, context, options.tintColor);

        // Convert sector patch to ImageBitmap for GPU compositing
        const bitmap = await createImageBitmap(canvas);

        const newBounds = {
          x: renderX / coordinateScale,
          y: renderY / coordinateScale,
          width: renderWidth / coordinateScale,
          height: renderHeight / coordinateScale
        };

        // 4. UPDATE CACHE: Store this patch (Limit cache size to ~12 items to manage RAM)
        if (patchCache.current.size > 12) {
          const firstKey = patchCache.current.keys().next().value;
          const evicted = patchCache.current.get(firstKey);
          safeCloseBitmap(evicted?.image);
          patchCache.current.delete(firstKey);
        }
        patchCache.current.set(cacheKey, { image: bitmap, bounds: newBounds });

        setHighResBounds(newBounds);
        setHighResImageWithCleanup(bitmap);

      } catch (err) {
        if (err.name !== 'RenderingCancelledException') {
          console.error("High-res overlay render failed", err);
        }
      } finally {
        setIsRenderingHighRes(false);
      }
    }, debounceMs);
  }, [pdfDoc, currentPageData, basePdfImage, pdfDimensions, containerDimensions, debounceMs, isInteracting]);

  // Cleanup pending tasks on unmount
  useEffect(() => {
    return () => {
      if (renderTimeout.current) clearTimeout(renderTimeout.current);
      if (highResRenderTask.current) highResRenderTask.current.cancel();
    };
  }, []);

  return {
    basePdfImage,
    pdfDimensions,
    highResImage,
    highResBounds,
    isPdfLoading,
    isRenderingHighRes,
    isLowEndDevice,
    loadingStatus,
    pdfError,
    updateHighResViewport
  };
};
